import { useState } from 'react'
import { useAuth } from 'context/authentication/useAuth'
import { featureFlagService } from 'utils/featureFlagService'
import { IconType } from 'react-icons'
import styled from 'styled-components'
import { MdLogout } from 'react-icons/md'
import { Link } from 'react-router-dom'
import { useIsActive } from 'hooks/useIsActive'
import {
  PiQuestion,
  PiHouse,
  PiHouseFill,
  PiHandshake,
  PiHandshakeFill,
  PiFolder,
  PiFolderFill,
  PiGear,
  PiGearFill,
  PiCaretDown,
  PiCaretUp,
  PiHandCoins,
  PiHandCoinsFill,
} from 'react-icons/pi'

interface StyledNavProps {
  $isOpen: boolean
}

const Overlay = styled.div<StyledNavProps>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(6, 81, 47, 0.16);
  opacity: ${(props) => (props.$isOpen ? '1' : '0')};
  visibility: ${(props) => (props.$isOpen ? 'visible' : 'hidden')};
  transition:
    opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    visibility 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 999;
`

const MenuContainer = styled.nav<StyledNavProps>`
  position: fixed;
  top: 12px;
  left: 12px;
  bottom: calc(60px + env(safe-area-inset-bottom) + 12px);
  width: 200px;
  background: white;
  box-shadow: 0px 6px 58px 0px #6e6e6e33;
  transform: translateX(${(props) => (props.$isOpen ? '0' : '-100%')});
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  border-radius: 12px;
`

const MenuHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  gap: 1rem;
`

const MenuTitle = styled.h2`
  margin: 0;
  font-size: 1rem;
  color: #191d23;
  font-weight: 600;
`

const MenuList = styled.ul`
  list-style: none;
  padding: 1rem 0;
  margin: 0;
  flex: 1;
  overflow-y: auto;
`

const BottomMenu = styled.div`
  color: #191d23;
  list-style: none;
  padding: 1rem 0;
  margin: 0;
  overflow-y: auto;
`

const MenuItem = styled.li`
  margin: 0;
  list-style: none;
`

interface MenuLinkProps {
  $isActive?: boolean
}

const MenuLink = styled(Link)<MenuLinkProps>`
  display: flex;
  align-items: center;
  margin: 0.5rem 1rem;
  padding: 0.5rem 1rem;
  text-decoration: none;
  font-size: 1rem;

  transition: all 0.2s ease;
  cursor: pointer;
  border-radius: 6px;
  color: ${(props) => (props.$isActive ? '#191D23' : '#6E6E6E')};
  background: ${(props) => (props.$isActive ? '#e6f1ec' : 'inherit')};
  font-weight: ${(props) => (props.$isActive ? 'bold' : 'inherit')};

  &:hover {
    background: #e6f1ec;
    font-weight: bold;
  }

  svg {
    margin-right: 1rem;
    font-size: 22px;
  }
`

const AccordionHeader = styled.div<MenuLinkProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0.5rem 1rem;
  padding: 0.5rem 1rem;
  font-size: 1rem;
  transition: all 0.2s ease;
  cursor: pointer;
  border-radius: 6px;
  color: ${(props) => (props.$isActive ? '#191D23' : '#6E6E6E')};
  background: ${(props) => (props.$isActive ? '#e6f1ec' : 'inherit')};
  font-weight: ${(props) => (props.$isActive ? 'bold' : 'inherit')};

  &:hover {
    background: #e6f1ec;
  }
`

const AccordionLeft = styled.div`
  display: flex;
  align-items: center;

  svg {
    margin-right: 1rem;
    font-size: 22px;
  }
`

const AccordionContent = styled.div<{ $isOpen: boolean }>`
  max-height: ${(props) => (props.$isOpen ? '300px' : '0')};
  overflow: hidden;
  transition: max-height 0.3s ease;
`

const SubMenuItem = styled(Link)<MenuLinkProps>`
  display: flex;
  align-items: center;
  margin: 0.25rem 1rem 0.25rem 2.5rem;
  padding: 0.5rem 1rem;
  text-decoration: none;
  font-size: 0.9rem;
  color: ${(props) => (props.$isActive ? '#191D23' : '#6E6E6E')};
  background: ${(props) => (props.$isActive ? '#e6f1ec' : 'inherit')};
  font-weight: ${(props) => (props.$isActive ? 'bold' : 'inherit')};
  border-radius: 6px;
  cursor: pointer;

  &:hover {
    background: #e6f1ec;
  }

  svg {
    margin-right: 0.75rem;
    font-size: 18px;
  }
`

const HelpCenterLink = styled(Link)`
  display: flex;
  align-items: center;
  margin: 0.5rem 1rem;
  padding: 0.5rem 1rem;
  color: white;
  text-decoration: none;
  font-size: 1rem;
  cursor: pointer;
  border-radius: 6px;
  background: #2c6b4e;

  svg {
    margin-right: 1rem;
    font-size: 22px;
  }
`

const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  margin: 0.5rem 1rem;
  padding: 0.5rem 1rem;
  text-decoration: none;
  font-size: 1rem;
  transition: all 0.2s ease;
  cursor: pointer;
  border-radius: 6px;
  color: #6e6e6e;
  background: transparent;
  border: none;
  width: calc(100% - 2rem);

  &:hover {
    background: #e6f1ec;
  }

  svg {
    margin-right: 1rem;
    font-size: 22px;
  }
`

export type MobileNavItem = {
  label: string
  icon: IconType
  activeIcon: IconType
  to: string
}

type Props = {
  isOpen: boolean
  items?: MobileNavItem[]
  onClose?: () => void
}

export default function MobileSlideOutMenu({ isOpen, onClose }: Props) {
  const { logout, user } = useAuth()
  const getIsActive = useIsActive()
  const { enableEmailSequencing } = featureFlagService()
  const [workspaceOpen, setWorkspaceOpen] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)

  const showEmailOutreach =
    enableEmailSequencing && user?.company?.has_bison_workspace

  const handleOverlayClick = () => {
    onClose?.()
  }

  const logoUrl = user?.company?.logos?.[0]?.logo_url

  // Check if any workspace sub-item is active
  const isWorkspaceActive =
    getIsActive('/documents') ||
    getIsActive('/products') ||
    getIsActive('/contacts') ||
    getIsActive('/reminders')

  return (
    <>
      <Overlay $isOpen={isOpen} onClick={handleOverlayClick} />
      <MenuContainer $isOpen={isOpen}>
        <MenuHeader>
          {logoUrl && <img src={logoUrl} alt="Company Logo" width="20px" />}
          <MenuTitle>{user?.company?.name}</MenuTitle>
        </MenuHeader>

        <MenuList>
          {/* Home */}
          <MenuItem>
            <MenuLink to="/" $isActive={getIsActive('/')}>
              {getIsActive('/') ? <PiHouseFill /> : <PiHouse />}
              Home
            </MenuLink>
          </MenuItem>

          {/* Samples */}
          <MenuItem>
            <MenuLink to="/samples" $isActive={getIsActive('/samples')}>
              {getIsActive('/samples') ? <PiHandCoinsFill /> : <PiHandCoins />}
              Samples
            </MenuLink>
          </MenuItem>

          {/* Deals */}
          <MenuItem>
            <MenuLink to="/deals" $isActive={getIsActive('/deals')}>
              {getIsActive('/deals') ? <PiHandshakeFill /> : <PiHandshake />}
              Deals
            </MenuLink>
          </MenuItem>

          {/* Workspace Accordion */}
          <MenuItem>
            <AccordionHeader
              $isActive={isWorkspaceActive}
              onClick={() => setWorkspaceOpen(!workspaceOpen)}
            >
              <AccordionLeft>
                {isWorkspaceActive ? <PiFolderFill /> : <PiFolder />}
                Workspace
              </AccordionLeft>
              {workspaceOpen ? <PiCaretUp /> : <PiCaretDown />}
            </AccordionHeader>
            <AccordionContent $isOpen={workspaceOpen}>
              <SubMenuItem
                to="/documents"
                $isActive={getIsActive('/documents')}
              >
                Documents
              </SubMenuItem>
              <SubMenuItem to="/products" $isActive={getIsActive('/products')}>
                Products
              </SubMenuItem>
              <SubMenuItem to="/contacts" $isActive={getIsActive('/contacts')}>
                Contacts
              </SubMenuItem>
              <SubMenuItem
                to="/reminders"
                $isActive={getIsActive('/reminders')}
              >
                Reminders
              </SubMenuItem>
            </AccordionContent>
          </MenuItem>

          {/* Settings Accordion */}
          <MenuItem>
            <AccordionHeader
              $isActive={getIsActive('/settings')}
              onClick={() => setSettingsOpen(!settingsOpen)}
            >
              <AccordionLeft>
                {getIsActive('/settings') ? <PiGearFill /> : <PiGear />}
                Settings
              </AccordionLeft>
              {settingsOpen ? <PiCaretUp /> : <PiCaretDown />}
            </AccordionHeader>
            <AccordionContent $isOpen={settingsOpen}>
              <SubMenuItem to="/settings" $isActive={getIsActive('/settings')}>
                Company
              </SubMenuItem>
              <SubMenuItem to="/users" $isActive={getIsActive('/users')}>
                Users
              </SubMenuItem>
              <SubMenuItem
                to="/integrations"
                $isActive={getIsActive('/integrations')}
              >
                Integrations
              </SubMenuItem>
              <SubMenuItem
                to="/forecasts"
                $isActive={getIsActive('/forecasts')}
              >
                Forecasts
              </SubMenuItem>
              <SubMenuItem
                to="/notifications"
                $isActive={getIsActive('/notifications')}
              >
                Notifications
              </SubMenuItem>
              <SubMenuItem
                to="/automations"
                $isActive={getIsActive('/automations')}
              >
                Automations
              </SubMenuItem>
              {showEmailOutreach && (
                <SubMenuItem
                  to="/settings/email-outreach/sender-emails"
                  $isActive={getIsActive('/settings/email-outreach')}
                >
                  Email Outreach
                </SubMenuItem>
              )}
            </AccordionContent>
          </MenuItem>
        </MenuList>

        <BottomMenu>
          <MenuItem>
            <HelpCenterLink to="#">
              <PiQuestion size={22} />
              Help Center
            </HelpCenterLink>
          </MenuItem>

          <MenuItem>
            <LogoutButton onClick={logout}>
              <MdLogout />
              Logout
            </LogoutButton>
          </MenuItem>
        </BottomMenu>
      </MenuContainer>
    </>
  )
}
